import { NetworkStatus, useQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  DataTable,
  DateUI,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import type { AdminAssetRulesRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const adminAssetRulesDocument = graphql(`
  query AdminAssetRules(
    $after: String
    $first: Int!
    $filter: AssetRulesPageFilter!
  ) {
    assetRules(after: $after, first: $first, filter: $filter) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      assetRules {
        ...AdminAssetRulesRow
      }
    }
  }

  fragment AdminAssetRulesRow on AssetRule {
    id
    name
    status
    evaluationStatus
    nextEvaluationDueAt
    asset {
      id
      businessEntity {
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      ...AssetCardFields
    }
    ... on RecurringWithdrawalAssetRule {
      beneficiaryAccount {
        id
        organisation {
          id
          name
          squareLogoUrl
        }
        ...BeneficiaryAssetCardFields
      }
    }
  }
`);

const DATA_TABLE_PAGE_SIZE = 10_000;

export const Route = createFileRoute("/__auth/asset-rules")({
  head: () => opsHead("Asset Rules"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminAssetRulesRowFragment>[] = [
  {
    id: "organisation",
    header: "Organisation",
    cell: ({ row }) => {
      const org =
        row.original.asset?.businessEntity?.organisation ??
        (row.original.__typename === "RecurringWithdrawalAssetRule"
          ? row.original.beneficiaryAccount?.organisation
          : undefined);
      return org ? (
        <div className="max-w-48">
          <OrganisationCard organisation={org} />
        </div>
      ) : (
        <Txt variant="caption" tone="secondary">
          —
        </Txt>
      );
    },
  },
  {
    id: "account",
    header: "Account",
    cell: ({ row }) => {
      if (row.original.asset) {
        return (
          <AssetCard
            asset={row.original.asset}
            subText={["BUSINESS_ENTITY", "BALANCE"]}
          />
        );
      }

      if (
        row.original.__typename === "RecurringWithdrawalAssetRule" &&
        row.original.beneficiaryAccount
      ) {
        return (
          <AssetCard
            beneficiaryAccount={row.original.beneficiaryAccount}
            subText="BUSINESS_ENTITY"
          />
        );
      }

      return "—";
    },
  },
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => row.original.name,
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => {
      switch (row.original.__typename) {
        case "TopUpAssetRule":
          return "Top-up";
        case "AlertAssetRule":
          return "Alert";
        case "RecurringTransferAssetRule":
          return "Recurring transfer";
        case "RecurringWithdrawalAssetRule":
          return "Recurring withdrawal";
        case "SweepAssetRule":
          return "Sweep";
        default:
          return neverFunc(row.original);
      }
    },
  },
  {
    id: "nextEvaluationDueAt",
    header: "Next evaluation",
    cell: ({ row }) => (
      <Txt variant="body" nowrap>
        <DateUI date={row.original.nextEvaluationDueAt} />
      </Txt>
    ),
  },
  {
    id: "evaluationStatus",
    header: "Eval health",
    cell: ({ row }) =>
      row.original.evaluationStatus ? (
        <StatusUI status={row.original.evaluationStatus} />
      ) : (
        <Txt variant="caption" tone="secondary">
          —
        </Txt>
      ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <StatusUI status={row.original.status} />,
  },
];

function View() {
  const navigate = useNavigate();

  const baseInput = {
    first: 50,
    filter: {
      status: ["ACTIVE" as const],
    },
  };

  const { data, fetchMore, loading, networkStatus } = useQuery(
    adminAssetRulesDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const evalStatusOrder: Record<string, number> = {
    IN_PROGRESS: 0,
    BREACH: 1,
    HEALTHY: 2,
  };

  const assetRules = [...(data?.assetRules?.assetRules ?? [])].sort((a, b) => {
    const aOrder = a.evaluationStatus
      ? (evalStatusOrder[a.evaluationStatus] ?? 3)
      : 3;
    const bOrder = b.evaluationStatus
      ? (evalStatusOrder[b.evaluationStatus] ?? 3)
      : 3;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    const aNext = a.nextEvaluationDueAt
      ? new Date(a.nextEvaluationDueAt).getTime()
      : Infinity;
    const bNext = b.nextEvaluationDueAt
      ? new Date(b.nextEvaluationDueAt).getTime()
      : Infinity;
    return aNext - bNext;
  });
  const nextCursor = data?.assetRules?.pageInfo.hasNextPage
    ? (data.assetRules.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <AdminPageContainer testId="admin-asset-rules">
      <AdminPageHeader title="Asset Rules" subtitle="Active rules" />

      <DataTable
        data={assetRules}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualPagination
        hidePagination
        pageSize={DATA_TABLE_PAGE_SIZE}
        className="overflow-hidden border"
        onRowClick={(row) => {
          navigate({
            to: "/asset-rules/$ruleId",
            params: { ruleId: row.original.id },
          });
        }}
      />

      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({
                variables: {
                  after: nextCursor,
                  ...baseInput,
                },
              })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!assetRules.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}
